import {
  useGetConnectionHealthSettingsQuery,
  useUpdateConnectionHealthSettingsMutation
} from '@axilon-apps/domains-ontology-ui-state'

import { Card, CardContent } from '../../../components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import { buildCadenceOptions, DEFAULT_CADENCE_MS, formatCadence } from './cadence'

/**
 * Org-wide connection-health check interval (Design §9). Reads / writes the
 * proxy `default_frequency_ms` setting; a change takes effect fleet-wide on the
 * next runner tick — no re-registration. Per-check `frequency_ms` overrides stay
 * API-only for now.
 */
export function ConnectionHealthCadenceCard() {
  const { data: settings, isLoading, isError } = useGetConnectionHealthSettingsQuery()
  const [updateSettings, { isLoading: isSaving }] = useUpdateConnectionHealthSettingsMutation()

  if (isLoading) {
    return (
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-ink">Check interval</h3>
          <p className="text-xs text-ink-3">Loading connection-health settings…</p>
        </CardContent>
      </Card>
    )
  }

  if (isError || !settings) {
    return (
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-ink">Check interval</h3>
          <p className="text-xs text-ink-3">
            Connection-health settings are unavailable. Check the proxy connection and reload.
          </p>
        </CardContent>
      </Card>
    )
  }

  const currentMs = settings.default_frequency_ms || DEFAULT_CADENCE_MS
  const options = buildCadenceOptions(currentMs)

  const onSelect = (raw: string) => {
    const next = Number(raw)
    if (!Number.isFinite(next) || next <= 0 || next === currentMs) return
    void updateSettings({ default_frequency_ms: next })
  }

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold text-ink">Check interval</h3>
          <p className="text-xs text-ink-3">
            How often every connection-health check runs. Applies to your whole organization and
            takes effect on the next check tick — no re-registration needed.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-ink-2">
            Currently every <span className="font-medium text-ink">{formatCadence(currentMs)}</span>
          </span>
          <div className="w-40">
            <Select value={String(currentMs)} onValueChange={onSelect} disabled={isSaving}>
              <SelectTrigger aria-label="Check interval">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
